import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, AlertCircle, ExternalLink, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ListConfig, ListResponse, CompanyAggregateResponse, CompanyColumn, PageColumn, ListResult, GroupedCompany, CompanyAggregateResult } from './types';

interface ListRendererProps {
  config: ListConfig;
  data?: ListResponse | CompanyAggregateResponse;
  isLoading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  actualTotal?: number;
  currentSort?: { field: string; order: 'asc' | 'desc' };
  onSortChange?: (field: string) => void;
}

// Column label mapping
const COLUMN_LABELS: Record<CompanyColumn | PageColumn, string> = {
  slug: 'Company',
  category: 'Category',
  employees: 'Size',
  page_count: '# Pages',
  total_plans: 'Total Plans',
  total_addons: 'Total Add-ons',
  avg_plans_per_page: 'Avg Plans/Page',
  addon_count: 'Add-ons',
  plan_count: 'Plans',
  has_freemium: 'Freemium',
  has_trial: 'Free Trial',
  has_license: 'License',
  has_usage: 'Usage',
  has_public_pricing: 'Public',
  has_contact_pricing: 'Contact',
};

// Group results by company_slug
function groupByCompany(results: ListResult[]): GroupedCompany[] {
  const groups = new Map<string, GroupedCompany>();

  results.forEach(result => {
    const existing = groups.get(result.company_slug);

    if (existing) {
      // Add page to existing company
      existing.pages.push(result);
      existing.page_count++;

      // Aggregate metrics
      existing.total_addon_count += result.metrics.addon_count;
      existing.total_plan_count += result.metrics.plan_count;
      existing.has_license = existing.has_license || result.metrics.has_license;
      existing.has_usage = existing.has_usage || result.metrics.has_usage;
      existing.has_freemium = existing.has_freemium || result.metrics.has_freemium;
      existing.has_trial = existing.has_trial || result.metrics.has_trial;
      existing.has_public_pricing = existing.has_public_pricing || result.metrics.has_public_pricing;
      existing.has_contact_pricing = existing.has_contact_pricing || result.metrics.has_contact_pricing;
    } else {
      // Create new company group
      groups.set(result.company_slug, {
        company_slug: result.company_slug,
        company: result.company,
        page_count: 1,
        pages: [result],
        total_addon_count: result.metrics.addon_count,
        total_plan_count: result.metrics.plan_count,
        has_license: result.metrics.has_license,
        has_usage: result.metrics.has_usage,
        has_freemium: result.metrics.has_freemium,
        has_trial: result.metrics.has_trial,
        has_public_pricing: result.metrics.has_public_pricing,
        has_contact_pricing: result.metrics.has_contact_pricing,
      });
    }
  });

  return Array.from(groups.values());
}

// Render cell content for company aggregate list (from /analytics/companies endpoint)
const renderCompanyAggregateCell = (column: CompanyColumn, result: CompanyAggregateResult): React.ReactNode => {
  const { company, metrics } = result;

  switch (column) {
    case 'slug':
      return (
        <div className="flex items-center">
          {company.logo_url && (
            <img
              src={company.logo_url}
              alt={company.name}
              className="h-8 w-8 rounded-full mr-3 object-cover"
            />
          )}
          <div className="text-sm font-medium text-gray-900">{company.name}</div>
        </div>
      );
    case 'category':
      return <span className="text-sm text-gray-500">{company.category_name || '-'}</span>;
    case 'employees':
      return <span className="text-sm text-gray-500">{company.employees || '-'}</span>;
    case 'page_count':
      return <span className="text-sm text-gray-900">{metrics.page_count}</span>;
    case 'total_plans':
      return <span className="text-sm text-gray-900">{metrics.total_plans}</span>;
    case 'total_addons':
      return <span className="text-sm text-gray-900">{metrics.total_addons}</span>;
    case 'avg_plans_per_page':
      return <span className="text-sm text-gray-900">{metrics.avg_plans_per_page.toFixed(1)}</span>;
    default:
      return '-';
  }
};

// Render cell content for company list (old grouped version - kept for compatibility)
const renderCompanyCell = (column: CompanyColumn, grouped: GroupedCompany): React.ReactNode => {
  const { company, page_count } = grouped;

  switch (column) {
    case 'slug':
      return (
        <div className="flex items-center">
          {company.logo_url && (
            <img
              src={company.logo_url}
              alt={company.name}
              className="h-8 w-8 rounded-full mr-3 object-cover"
            />
          )}
          <div className="text-sm font-medium text-gray-900">{company.name}</div>
        </div>
      );
    case 'category':
      return <span className="text-sm text-gray-500">{company.category_name || '-'}</span>;
    case 'employees':
      return <span className="text-sm text-gray-500">{company.employees || '-'}</span>;
    case 'page_count':
      return <span className="text-sm text-gray-900">{page_count}</span>;
    case 'total_plans':
      return <span className="text-sm text-gray-900">{grouped.total_plan_count}</span>;
    case 'total_addons':
      return <span className="text-sm text-gray-900">{grouped.total_addon_count}</span>;
    case 'avg_plans_per_page':
      return <span className="text-sm text-gray-900">{(grouped.total_plan_count / page_count).toFixed(1)}</span>;
    default:
      return '-';
  }
};

// Render cell content for page list
const renderPageCell = (column: PageColumn, result: ListResult): React.ReactNode => {
  const { company, metrics, page } = result;

  switch (column) {
    case 'slug':
      return (
        <div className="flex items-center">
          {company.logo_url && (
            <img
              src={company.logo_url}
              alt={company.name}
              className="h-8 w-8 rounded-full mr-3 object-cover"
            />
          )}
          <div>
            <div className="text-sm font-medium text-gray-900">{company.name}</div>
            {page.subslug && (
              <div className="text-xs text-gray-500">{page.subslug}</div>
            )}
          </div>
        </div>
      );
    case 'category':
      return <span className="text-sm text-gray-500">{company.category_name || '-'}</span>;
    case 'employees':
      return <span className="text-sm text-gray-500">{company.employees || '-'}</span>;
    case 'addon_count':
      return <span className="text-sm text-gray-900">{metrics.addon_count}</span>;
    case 'plan_count':
      return <span className="text-sm text-gray-900">{metrics.plan_count}</span>;
    case 'has_freemium':
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${metrics.has_freemium ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
          {metrics.has_freemium ? 'Yes' : 'No'}
        </span>
      );
    case 'has_trial':
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${metrics.has_trial ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
          {metrics.has_trial ? 'Yes' : 'No'}
        </span>
      );
    case 'has_license':
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${metrics.has_license ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
          {metrics.has_license ? 'Yes' : 'No'}
        </span>
      );
    case 'has_usage':
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${metrics.has_usage ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-500'}`}>
          {metrics.has_usage ? 'Yes' : 'No'}
        </span>
      );
    case 'has_public_pricing':
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${metrics.has_public_pricing ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
          {metrics.has_public_pricing ? 'Yes' : 'No'}
        </span>
      );
    case 'has_contact_pricing':
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${metrics.has_contact_pricing ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-500'}`}>
          {metrics.has_contact_pricing ? 'Yes' : 'No'}
        </span>
      );
    default:
      return '-';
  }
};

// Get text alignment based on column type
const getColumnAlignment = (column: CompanyColumn | PageColumn): 'left' | 'right' | 'center' => {
  if (['addon_count', 'plan_count', 'page_count', 'total_plans', 'total_addons', 'avg_plans_per_page'].includes(column)) {
    return 'right';
  }
  if (['has_freemium', 'has_trial', 'has_license', 'has_usage', 'has_public_pricing', 'has_contact_pricing'].includes(column)) {
    return 'center';
  }
  return 'left';
};

// Check if column is sortable based on backend API support
const isSortableColumn = (column: CompanyColumn | PageColumn, listType: 'company' | 'page'): boolean => {
  if (listType === 'company') {
    // Company list sort fields: company_name, page_count, total_plans, total_addons, avg_plans_per_page, has_freemium, has_trial, has_public_pricing, has_contact_pricing, has_license, has_usage
    return [
      'page_count',
      'total_plans',
      'total_addons',
      'avg_plans_per_page',
      'has_freemium',
      'has_trial',
      'has_public_pricing',
      'has_contact_pricing',
      'has_license',
      'has_usage'
    ].includes(column);
  } else {
    // Page list sort fields: company_name, addon_count, plan_count, modality_count, threshold_count, has_freemium, has_trial, has_public_pricing, has_contact_pricing, has_license, has_usage
    return [
      'addon_count',
      'plan_count',
      'modality_count',
      'threshold_count',
      'has_freemium',
      'has_trial',
      'has_public_pricing',
      'has_contact_pricing',
      'has_license',
      'has_usage'
    ].includes(column);
  }
};

// Map column names to sort field names
const getColumnSortField = (column: CompanyColumn | PageColumn, listType: 'company' | 'page'): string => {
  // Most columns map directly to their field name
  if (column === 'slug') return listType === 'company' ? 'company_name' : 'company_name';
  if (column === 'category') return 'company_name'; // Categories aren't sortable on backend, fall back to company name
  if (column === 'employees') return 'company_name'; // Employee ranges aren't sortable on backend
  return column;
};

export const ListRenderer: React.FC<ListRendererProps> = ({
  config,
  data,
  isLoading,
  error,
  onRefresh,
  onLoadMore,
  isLoadingMore,
  actualTotal,
  currentSort,
  onSortChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const itemsPerPage = 10;
  const collapsedItemCount = 3;

  // Process data based on list type
  const processedData = useMemo(() => {
    if (!data || !data.results) return null;

    if (config.listType === 'company') {
      // Check if we have CompanyAggregateResponse (from /analytics/companies)
      const firstResult = data.results[0];
      if (firstResult && 'metrics' in firstResult && 'pages' in firstResult) {
        // This is CompanyAggregateResponse
        return {
          type: 'company-aggregate' as const,
          items: data.results as CompanyAggregateResult[],
          total: actualTotal ?? data.pagination.total,
        };
      } else {
        // This is ListResponse, need to group
        const groupedItems = groupByCompany(data.results as ListResult[]);
        return {
          type: 'company' as const,
          items: groupedItems,
          total: actualTotal ?? groupedItems.length,
        };
      }
    } else {
      // Use raw results for page list
      return {
        type: 'page' as const,
        items: data.results as ListResult[],
        total: actualTotal ?? data.pagination.total,
      };
    }
  }, [data, config.listType, actualTotal]);

  // Auto-load more when navigating near the end of loaded results
  useEffect(() => {
    if (!processedData || !onLoadMore) return;

    const totalLoadedPages = Math.ceil(processedData.items.length / itemsPerPage);
    const isNearEnd = currentPage >= totalLoadedPages - 1; // Within 1 page of end
    const hasMore = data?.pagination.has_more;

    if (isNearEnd && hasMore && !isLoadingMore) {
      onLoadMore();
    }
  }, [currentPage, processedData, onLoadMore, isLoadingMore, itemsPerPage, data]);

  // Loading state - show loading if isLoading OR if we don't have data yet
  if (isLoading || (!data && !error)) {
    return (
      <div className="w-full">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full">
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-red-900">Failed to load list</h4>
            <p className="text-sm text-red-700 mt-1">{error.message}</p>
            {onRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                className="mt-3"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!processedData || processedData.items.length === 0) {
    return (
      <div className="w-full text-center py-8 text-gray-500">
        <p>No {config.listType === 'company' ? 'companies' : 'pricing pages'} match the selected filters</p>
      </div>
    );
  }

  // Pagination logic - handle collapsed vs expanded state
  const currentItems = isCollapsed
    ? processedData.items.slice(0, collapsedItemCount)
    : processedData.items.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const totalPages = Math.ceil(processedData.items.length / itemsPerPage);
  const title = config.title || (config.listType === 'company' ? 'Companies' : 'Pricing Pages');
  const hasMoreItems = processedData.items.length > collapsedItemCount;

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {processedData.total.toLocaleString()} {config.listType === 'company' ? 'companies' : 'pages'} total
            {processedData.items.length < processedData.total && (
              <span className="text-gray-400"> ({processedData.items.length.toLocaleString()} loaded)</span>
            )}
          </p>
        </div>
        {onRefresh && (
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {config.columns.map((column) => {
                const alignment = getColumnAlignment(column);
                const sortable = isSortableColumn(column, config.listType);
                const sortField = getColumnSortField(column, config.listType);
                const isCurrentSort = currentSort?.field === sortField;
                const sortOrder = isCurrentSort ? currentSort.order : undefined;

                return (
                  <th
                    key={column}
                    scope="col"
                    className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      alignment === 'right' ? 'text-right' :
                      alignment === 'center' ? 'text-center' :
                      'text-left'
                    } ${sortable && onSortChange ? 'cursor-pointer hover:bg-gray-100 select-none' : ''}`}
                    onClick={() => sortable && onSortChange && onSortChange(sortField)}
                  >
                    <div className={`flex items-center gap-1 ${
                      alignment === 'right' ? 'justify-end' :
                      alignment === 'center' ? 'justify-center' :
                      'justify-start'
                    }`}>
                      <span>{COLUMN_LABELS[column]}</span>
                      {sortable && onSortChange && (
                        <span className="inline-flex">
                          {isCurrentSort ? (
                            sortOrder === 'asc' ? (
                              <ArrowUp className="h-3 w-3 text-blue-600" />
                            ) : (
                              <ArrowDown className="h-3 w-3 text-blue-600" />
                            )
                          ) : (
                            <ArrowUpDown className="h-3 w-3 text-gray-400" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Link
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {processedData.type === 'company-aggregate' ? (
              // Render company aggregate rows (from /analytics/companies)
              (currentItems as CompanyAggregateResult[]).map((result) => (
                <tr key={result.company.id} className="hover:bg-gray-50">
                  {(config.columns as CompanyColumn[]).map((column) => {
                    const alignment = getColumnAlignment(column);
                    return (
                      <td
                        key={column}
                        className={`px-4 py-4 whitespace-nowrap ${
                          alignment === 'right' ? 'text-right' :
                          alignment === 'center' ? 'text-center' :
                          ''
                        }`}
                      >
                        {renderCompanyAggregateCell(column, result)}
                      </td>
                    );
                  })}
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                    <a
                      href={result.pages[0]?.pricing_page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </td>
                </tr>
              ))
            ) : processedData.type === 'company' ? (
              // Render company rows (grouped from list endpoint)
              (currentItems as GroupedCompany[]).map((grouped) => (
                <tr key={grouped.company_slug} className="hover:bg-gray-50">
                  {(config.columns as CompanyColumn[]).map((column) => {
                    const alignment = getColumnAlignment(column);
                    return (
                      <td
                        key={column}
                        className={`px-4 py-4 whitespace-nowrap ${
                          alignment === 'right' ? 'text-right' :
                          alignment === 'center' ? 'text-center' :
                          ''
                        }`}
                      >
                        {renderCompanyCell(column, grouped)}
                      </td>
                    );
                  })}
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                    <a
                      href={grouped.pages[0].page.pricing_page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </td>
                </tr>
              ))
            ) : (
              // Render page rows
              (currentItems as ListResult[]).map((result) => (
                <tr key={result.slug + result.page.id} className="hover:bg-gray-50">
                  {(config.columns as PageColumn[]).map((column) => {
                    const alignment = getColumnAlignment(column);
                    return (
                      <td
                        key={column}
                        className={`px-4 py-4 whitespace-nowrap ${
                          alignment === 'right' ? 'text-right' :
                          alignment === 'center' ? 'text-center' :
                          ''
                        }`}
                      >
                        {renderPageCell(column, result)}
                      </td>
                    );
                  })}
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                    <a
                      href={result.page.pricing_page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Expand Button */}
      {hasMoreItems && isCollapsed && (
        <div className="flex justify-center pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(false)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Pagination - only show when expanded */}
      {!isCollapsed && (totalPages > 1 || processedData.items.length < processedData.total) && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
          <div className="text-sm text-gray-700">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, processedData.items.length)} of {processedData.total.toLocaleString()}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages && !data?.pagination.has_more}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Collapse Button - show when expanded */}
      {!isCollapsed && (
        <div className="flex justify-center pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setIsCollapsed(true);
              setCurrentPage(1); // Reset to first page when collapsing
            }}
            className="text-gray-600 hover:text-gray-900"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
